/**
 * Data Access Layer for People
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type { Person, PersonCreate, PersonUpdate } from './types.js';
import { resolveSlugAlias } from './slug-aliases.js';

export async function getPerson(id: string): Promise<Person | null> {
  let query = getClient()
    .from(TABLES.people)
    .select('*')
    .eq('id', id);

  // Multi-tenant: Filter by account_id if account context is set
  const accountId = getCurrentAccountId();
  if (accountId) {
    query = query.eq('account_id', accountId);
  }

  const { data, error } = await query.single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as Person;
}

export async function getPersonBySlug(slug: string): Promise<Person | null> {
  let query = getClient()
    .from(TABLES.people)
    .select('*')
    .eq('slug', slug);

  // Multi-tenant: Filter by account_id if account context is set
  const accountId = getCurrentAccountId();
  if (accountId) {
    query = query.eq('account_id', accountId);
  }

  const { data, error } = await query.single();

  if (error) {
    if (error.code === 'PGRST116') {
      // Not found by current slug — try slug aliases
      const entityId = await resolveSlugAlias('person', slug);
      if (entityId) {
        return getPerson(entityId);
      }
      return null;
    }
    throw error;
  }

  return data as Person;
}

export async function getPersonByProfileToken(token: string): Promise<Person | null> {
  const { data, error } = await getClient()
    .from(TABLES.people)
    .select('*')
    .eq('profile_completion_token', token)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as Person;
}

export async function listPeople(filters?: {
  type?: string;
  is_active?: boolean;
  is_public?: boolean;
  is_collective?: boolean;
  query?: string;
  limit?: number;
}): Promise<Person[]> {
  let query = getClient().from(TABLES.people).select('*');

  // Multi-tenant: Filter by account_id if account context is set
  const accountId = getCurrentAccountId();
  if (accountId) {
    query = query.eq('account_id', accountId);
  }

  if (filters?.type) {
    query = query.eq('type', filters.type);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }
  if (filters?.is_public !== undefined) {
    query = query.eq('is_public', filters.is_public);
  }
  if (filters?.is_collective !== undefined) {
    query = query.eq('is_collective', filters.is_collective);
  }
  if (filters?.query && filters.query.trim()) {
    const searchTerm = filters.query.trim();
    // PostgREST or() syntax: comma-separated conditions
    // The client library should handle % wildcards for ilike
    query = query.or(`name.ilike.%${searchTerm}%,slug.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
  }

  query = query.order('name', { nullsFirst: false });

  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data || []) as Person[];
}

export async function createPerson(input: PersonCreate): Promise<Person> {
  const { data, error } = await getClient()
    .from(TABLES.people)
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  const person = data as Person;

  // Trigger webhook if this is a public artist
  if (person.type === 'artist' && person.is_public) {
    const { triggerWebsiteUpdate } = await import('./webhook.js');
    triggerWebsiteUpdate('artists-updated', {
      personId: person.id,
      artistSlug: person.slug,
    });
  }

  return person;
}

export async function updatePerson(id: string, input: PersonUpdate): Promise<Person> {
  const { data, error } = await getClient()
    .from(TABLES.people)
    .update(input)
    .select()
    .eq('id', id)
    .single();

  if (error) throw error;
  const person = data as Person;

  // Trigger webhook if this is a public artist
  if (person.type === 'artist' && person.is_public) {
    const { triggerWebsiteUpdate } = await import('./webhook.js');
    triggerWebsiteUpdate('artists-updated', {
      personId: person.id,
      artistSlug: person.slug,
    });
  }

  return person;
}

export async function updatePersonByToken(token: string, input: PersonUpdate): Promise<Person> {
  const completedAt = new Date().toISOString();

  const { data, error } = await getClient()
    .from(TABLES.people)
    .update({
      ...input,
      profile_completed_at: completedAt,
    })
    .eq('profile_completion_token', token)
    .select()
    .single();

  if (error) throw error;

  // The scheduler will pick up the completion and send notifications to configured staff
  return data as Person;
}

export async function deletePerson(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.people)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function deletePeople(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  
  const { error } = await getClient()
    .from(TABLES.people)
    .delete()
    .in('id', ids);

  if (error) throw error;
}
